import { Link } from 'react-router-dom'
import { useFilamentSpools, FILAMENT_MATERIALS } from '../../hooks/useFilamentSpools'
import { useSettings } from '../../hooks/useSettings'
import type { FilamentSpool } from '../../lib/database.types'
import { Skeleton } from '../../components/ui'

// Display label per material — keeps "PLA Basic" terminology that 3D-print
// hobbyists actually use, distinct from PLA+ / PLA Silk / etc.
const MATERIAL_LABEL: Record<string, string> = {
  PLA: 'PLA Basic',
}

function materialLabel(m: string): string {
  return MATERIAL_LABEL[m] ?? m
}

// One card per (brand, material, color, hex) so 4 identical black spools
// collapse into a single "×4" card instead of 4 separate ones.
interface SpoolGroup {
  key: string
  rep: FilamentSpool
  spools: FilamentSpool[]
  totalRemaining: number
  totalOriginal: number
}

function identityKey(s: FilamentSpool): string {
  return [
    (s.brand ?? '').trim().toLowerCase(),
    s.material,
    s.color.trim().toLowerCase(),
    s.color_hex ?? '',
  ].join('|')
}

function groupByIdentity(spools: FilamentSpool[]): SpoolGroup[] {
  const groups = new Map<string, SpoolGroup>()
  for (const s of spools) {
    const key = identityKey(s)
    const existing = groups.get(key)
    if (existing) {
      existing.spools.push(s)
      existing.totalRemaining += s.current_weight_grams
      existing.totalOriginal += s.original_weight_grams
      // Keep the most-recently-created as the "tap target" (newest data wins).
      if (s.created_at > existing.rep.created_at) existing.rep = s
    } else {
      groups.set(key, {
        key,
        rep: s,
        spools: [s],
        totalRemaining: s.current_weight_grams,
        totalOriginal: s.original_weight_grams,
      })
    }
  }
  return Array.from(groups.values())
}

function groupByMaterial(spools: FilamentSpool[]): Map<string, FilamentSpool[]> {
  const groups = new Map<string, FilamentSpool[]>()
  for (const m of FILAMENT_MATERIALS) groups.set(m, [])
  for (const s of spools) {
    const key = (FILAMENT_MATERIALS as readonly string[]).includes(s.material)
      ? s.material
      : 'Other'
    const list = groups.get(key) ?? []
    list.push(s)
    groups.set(key, list)
  }
  for (const [k, v] of groups) if (v.length === 0) groups.delete(k)
  return groups
}

export default function FilamentList() {
  const { data: spools, isLoading } = useFilamentSpools()
  const { data: settings } = useSettings()

  if (isLoading) return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 8 }}>
      {Array.from({ length: 8 }, (_, i) => (
        <Skeleton key={i} height={80} />
      ))}
    </div>
  )

  const lowThreshold = settings?.low_filament_threshold_grams ?? 100
  const active = (spools ?? []).filter((s) => s.status === 'in-use')
  const finished = (spools ?? []).filter((s) => s.status !== 'in-use')
  const activeGroups = groupByMaterial(active)
  const finishedGroups = groupByIdentity(finished)

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--space-4)' }}>
        <Link
          to="/prints/filament/new"
          style={{
            padding: 'var(--space-2) var(--space-4)',
            background: 'var(--color-primary)',
            color: 'white',
            borderRadius: 'var(--radius-md)',
            fontWeight: 600,
            fontSize: 'var(--font-size-sm)',
            textDecoration: 'none',
          }}
        >
          + Add spool
        </Link>
      </div>

      {active.length === 0 ? (
        <div
          style={{
            padding: 'var(--space-8)',
            textAlign: 'center',
            background: 'var(--color-surface)',
            borderRadius: 'var(--radius-lg)',
            color: 'var(--color-text-muted)',
          }}
        >
          No filament spools yet — tap &quot;Add spool&quot; to log your first.
        </div>
      ) : (
        <div
          style={{
            maxHeight: '70vh',
            overflowY: 'auto',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-3)',
            background: 'var(--color-bg)',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {Array.from(activeGroups.entries()).map(([material, materialSpools]) => {
            const identityGroups = groupByIdentity(materialSpools)
            const totalRemaining = materialSpools.reduce((sum, s) => sum + s.current_weight_grams, 0)
            return (
              <section key={material} style={{ marginBottom: 'var(--space-6)' }}>
                <header
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    padding: 'var(--space-2) var(--space-3)',
                    marginBottom: 'var(--space-2)',
                    borderBottom: '1px solid var(--color-border)',
                  }}
                >
                  <span style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 700,
                    color: 'var(--color-text)',
                  }}>
                    {materialLabel(material)}
                  </span>
                  <span style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-faint)',
                    fontVariantNumeric: 'tabular-nums',
                  }}>
                    {materialSpools.length} spool{materialSpools.length === 1 ? '' : 's'} · {totalRemaining}g remaining
                  </span>
                </header>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                    gap: 6,
                  }}
                >
                  {identityGroups.map((g) => (
                    <SpoolGroupCard key={g.key} group={g} lowThreshold={lowThreshold} />
                  ))}
                </div>
              </section>
            )
          })}

          {finished.length > 0 && (
            <>
              <h3
                style={{
                  fontSize: 'var(--font-size-base)',
                  color: 'var(--color-text-muted)',
                  margin: 'var(--space-6) 0 var(--space-3)',
                }}
              >
                Finished / archived
              </h3>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  gap: 'var(--space-2)',
                }}
              >
                {finishedGroups.map((g) => (
                  <SpoolGroupCard key={g.key} group={g} lowThreshold={lowThreshold} muted />
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

// Pick readable text color over an arbitrary hex background.
function isLightHex(hex: string): boolean {
  const h = hex.replace('#', '')
  if (h.length !== 6) return false
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  // Perceived brightness — anything above ~160 reads as "light".
  return (r * 299 + g * 587 + b * 114) / 1000 > 160
}

// Compact gram label so 1000g → "1k", 4000g → "4k", 1500g → "1.5k".
function compactGrams(g: number): string {
  if (g < 1000) return String(g)
  const kg = g / 1000
  return kg % 1 === 0 ? `${kg}k` : `${kg.toFixed(1)}k`
}

function SpoolGroupCard({
  group,
  lowThreshold,
  muted,
}: {
  group: SpoolGroup
  lowThreshold: number
  muted?: boolean
}) {
  const { rep, spools, totalRemaining, totalOriginal } = group
  const N = spools.length
  const pct =
    totalOriginal > 0
      ? Math.max(0, Math.min(100, (totalRemaining / totalOriginal) * 100))
      : 0
  // Low warning fires when the GROUP's remaining stock drops below the
  // single-spool threshold — i.e., you're almost out of this color entirely.
  const isLow = rep.status === 'in-use' && totalRemaining < lowThreshold

  // Card is the filament color. Text + ring tint flips for legibility on
  // light-coloured spools (white, yellow, sand…).
  const bg = rep.color_hex || '#3f3f46'
  const lightBg = isLightHex(bg)
  const textShadow = lightBg ? 'none' : '0 1px 2px rgba(0,0,0,0.65)'
  const ringTrack = lightBg ? 'rgba(0,0,0,0.18)' : 'rgba(255,255,255,0.22)'
  const innerBg = lightBg ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.55)'
  const innerColor = lightBg ? '#1a1a1a' : '#fff'
  const ringFill = isLow ? '#f59e0b' : '#8b5cf6'
  const nameColor = lightBg ? '#1a1a1a' : '#fafafa'
  const tagBg = 'rgba(0,0,0,0.5)'
  const tagColor = '#fff'

  // Split brand/color across 2 lines so long names don't get truncated.
  const nameLine1 = rep.brand?.trim() || rep.color.trim()
  const nameLine2 = rep.brand ? rep.color.trim() : ''
  const fullLabel = rep.brand ? `${rep.brand.trim()} ${rep.color.trim()}` : rep.color.trim()

  return (
    <Link
      to={`/prints/filament/${rep.id}`}
      style={{
        background: bg,
        borderRadius: 'var(--radius-md)',
        padding: 6,
        textDecoration: 'none',
        opacity: muted ? 0.5 : 1,
        border: isLow ? '1px solid #f59e0b' : '1px solid rgba(255,255,255,0.1)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        minWidth: 0,
        position: 'relative',
        overflow: 'hidden',
      }}
      title={`${fullLabel} · ${totalRemaining}g${N > 1 ? ` · ×${N}` : ''}${isLow ? ' · LOW' : ''}`}
    >
      {N > 1 && (
        <span
          style={{
            position: 'absolute',
            top: 3, right: 3,
            background: tagBg, color: tagColor,
            fontSize: 9, fontWeight: 700,
            padding: '0 4px', borderRadius: 999,
            lineHeight: '14px',
            zIndex: 2,
          }}
        >
          ×{N}
        </span>
      )}
      {isLow && (
        <span
          style={{
            position: 'absolute',
            top: 3, left: 3,
            background: tagBg, color: '#fcd34d',
            fontSize: 9, fontWeight: 700,
            padding: '0 4px', borderRadius: 999,
            lineHeight: '14px',
            zIndex: 2,
          }}
        >
          ▾ LOW
        </span>
      )}
      <div
        style={{
          width: 38,
          height: 38,
          borderRadius: '50%',
          background: `conic-gradient(${ringFill} 0% ${pct}%, ${ringTrack} ${pct}% 100%)`,
          position: 'relative',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 4,
            borderRadius: '50%',
            background: innerBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 10,
            fontWeight: 800,
            fontVariantNumeric: 'tabular-nums',
            color: innerColor,
            textShadow: lightBg ? 'none' : '0 1px 2px rgba(0,0,0,0.7)',
          }}
        >
          {compactGrams(totalRemaining)}
        </div>
      </div>
      <div
        style={{
          fontWeight: 700,
          fontSize: 9,
          lineHeight: 1.2,
          marginTop: 5,
          height: 22,
          overflow: 'hidden',
          color: nameColor,
          textShadow,
          width: '100%',
        }}
      >
        {nameLine1}
        {nameLine2 && <><br />{nameLine2}</>}
      </div>
    </Link>
  )
}
